import { getSimpleISOString } from "@prismicio/editor-support/Date"

import type { QuotaOrLimit, Subscription } from "../../app/services/billing"
import type { Usage } from "../../app/services/usage"
import type { Unit } from "./usageUtils"

interface UsageSummaryItemType {
  name: string
  unit: Unit
  unitLabel: string
  chartUnit: Unit
  chartYAxisLabel: string
  getQuota: (subscription: Subscription) => QuotaOrLimit
  getLimit: (subscription: Subscription) => QuotaOrLimit
  getTotalUsage: (usage: Usage) => number
  getUsageByDate: (usage: Usage) => UsageByDate[]
  formatQuotaDescription: (formattedQuota: string) => string
}

export interface UsageByDate extends Record<string, string | number> {
  date: string
  content: number
  media: number
}

export const usageSummaryItemTypes = {
  "cdn-bandwidth": {
    name: "CDN bandwidth",
    unit: "gigabyte",
    unitLabel: "gigabyte",
    chartUnit: "byte",
    chartYAxisLabel: "Bandwidth",
    getQuota: (subscription) => subscription.quotas.bandwidth,
    getLimit: (subscription) => subscription.limits.bandwidth,
    getTotalUsage: (usage) =>
      Number(usage.totalCloudFrontBandwidth + usage.totalImgixBandwidth) /
      1_000_000_000,
    getUsageByDate: (usage) =>
      usage.results.map((result) => ({
        date: getSimpleISOString(result.date),
        content: Number(result.cloudFrontBandwidth),
        media: Number(result.imgixBandwidth),
      })),
    formatQuotaDescription: (formattedQuota) =>
      `a monthly limit of ${formattedQuota} of CDN bandwidth`,
  },
  "api-calls": {
    name: "API calls",
    unit: undefined,
    unitLabel: "call",
    chartUnit: undefined,
    chartYAxisLabel: "Calls",
    getQuota: (subscription) => subscription.quotas.apiCalls,
    getLimit: (subscription) => subscription.limits.apiCalls,
    getTotalUsage: (usage) =>
      Number(usage.totalCloudFrontCalls + usage.totalImgixCalls),
    getUsageByDate: (usage) =>
      usage.results.map((result) => ({
        date: getSimpleISOString(result.date),
        content: Number(result.cloudFrontCalls),
        media: Number(result.imgixCalls),
      })),
    formatQuotaDescription: (formattedQuota) =>
      `${formattedQuota} API calls to the Prismic API`,
  },
} satisfies Record<string, UsageSummaryItemType>

interface UsageSummarySubItemType {
  name: string
  itemTypes: Record<
    keyof typeof usageSummaryItemTypes,
    { logsDescription: string }
  >
}

export const usageSummarySubItemTypes = {
  "content-api": {
    name: "Content API",
    itemTypes: {
      "api-calls": {
        logsDescription:
          "most requested files including json, svg, pdf and videos (API calls)",
      },
      "cdn-bandwidth": {
        logsDescription:
          "largest consuming files including json, svg, pdf and videos (bytes)",
      },
    },
  },
  "image-cdn": {
    name: "Image CDN",
    itemTypes: {
      "api-calls": { logsDescription: "most requested images (API calls)" },
      "cdn-bandwidth": { logsDescription: "largest consuming images (bytes)" },
    },
  },
} satisfies Record<string, UsageSummarySubItemType>
